import React from 'react';

import {
  Datagrid,
  DateField,
  Filter,
  FunctionField,
  Labeled,
  List,
  ReferenceField,
  SelectField,
  SelectInput,
  TextField,
  useRecordContext,
} from 'react-admin';

import {
  Box,
  Button,
  Card,
  Chip,
  Grid,
} from '@mui/material';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import resourceNames from '../../constants/resourceNames';
import Pagination from '../layout/Pagination';
import NoResults from '../layout/NoResults';
import { boldDataGridStyle } from '../../constants/style/datagridStyles';
import theme from '../../theme';
import { RuleTypes } from '../../constants/ruleTypes';
import { RuleViews } from '../../constants/ruleView';

const reviewActionChoices = [
  { id: 'RuleVersionPublish', name: 'Publish' },
  { id: 'RuleVersionRelease', name: 'Release' },
];

const reviewStatusChoices = [
  { id: 'pending', name: 'Pending' },
  { id: 'approved', name: 'Approved' },
  { id: 'rejected', name: 'Rejected' },
  { id: 'withdrawn', name: 'Withdrawn' },
];

const ReviewFilters = ({ context }: { context?: 'form' | 'button' }) => (
  <Filter context={context}>
    <SelectInput
      source="status"
      label="Status"
      choices={reviewStatusChoices}
      alwaysOn
    />
  </Filter>
);

const statusColorMap: Record<string, 'success' | 'error' | 'warning' | 'default'> = {
  approved: 'success',
  rejected: 'error',
  pending: 'warning',
  withdrawn: 'default',
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const StatusField = ({ label }: { label?: string }) => {
  const record = useRecordContext();
  if (!record) return null;
  const color = statusColorMap[record.status] ?? 'default';
  return <Chip label={record.status} color={color} size="small" />;
};

const ReviewExpandPanel = () => (
  <Box p={2}>
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Labeled>
          <TextField source="reasoning" label="Reasoning" emptyText="-" />
        </Labeled>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <Labeled>
          <TextField source="comment" label="Comment" emptyText="-" />
        </Labeled>
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Labeled>
          <TextField source="actionInformation.description" label="Description" emptyText="-" />
        </Labeled>
      </Grid>
    </Grid>
  </Box>
);

const PreviousRuleReviewList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const action = searchParams.get('action') ?? undefined;
  const { state } = useLocation() as {
    state?: { record?: { ruleType?: RuleTypes; view?: RuleViews } };
  };
  // Carries no version, so Rules opens on the latest live version of the rule type
  const backToRules = () => navigate('/rules', {
    state: { record: { ruleType: state?.record?.ruleType, view: state?.record?.view } },
  });

  return (
    <Box>
      <Box mb={2}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={backToRules}
        >
          Back to rules
        </Button>
      </Box>
      <List
        resource={resourceNames.REVIEWS}
        filters={<ReviewFilters />}
        filter={action ? { action } : undefined}
        sort={{ field: 'submittedAt', order: 'DESC' }}
        empty={false}
        pagination={<Pagination disableEmptyText />}
        exporter={false}
      >
        <Card sx={{ margin: 0 }} variant="outlined">
          <Datagrid
            bulkActionButtons={false}
            sx={{
              ...boldDataGridStyle,
              '& .RaDatagrid-expandedPanel': {
                backgroundColor: theme.palette.background.expand,
              },
            }}
            empty={<NoResults variant="h6" />}
            expand={<ReviewExpandPanel />}
          >
            <SelectField source="action" label="Action" choices={reviewActionChoices} sortable={false} />
            <StatusField label="Status" />
            <TextField source="actionInformation.description" label="Details" sortable={false} />
            <ReferenceField source="proposerId" reference={resourceNames.MEMBERS} link={false} label="Proposed by">
              <FunctionField render={(member: { firstName: string; lastName: string }) => `${member.firstName} ${member.lastName}`} />
            </ReferenceField>
            <ReferenceField source="resolverId" reference={resourceNames.MEMBERS} link={false} label="Resolved by" emptyText="-">
              <FunctionField render={(member: { firstName: string; lastName: string }) => `${member.firstName} ${member.lastName}`} />
            </ReferenceField>
            <DateField source="submittedAt" label="Submitted" showTime sortable />
            <DateField source="resolvedAt" label="Resolved" showTime sortable emptyText="-" />
          </Datagrid>
        </Card>
      </List>
    </Box>
  );
};

export default PreviousRuleReviewList;
